import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Derive a friendly label for an invalid form element. Priority:
 *   1. <label htmlFor={el.id}> text
 *   2. nearest ancestor div's first <label> text
 *   3. element name
 *   4. data-testid fallback
 * Returns null when nothing useful can be resolved (caller should skip).
 */
function resolveLabel(el) {
  if (!el) return null;
  try {
    if (el.labels && el.labels[0]?.innerText) return el.labels[0].innerText.replace(/\s*\*$/, '').trim();
    const nearby = el.closest('div')?.querySelector('label');
    if (nearby?.innerText) return nearby.innerText.replace(/\s*\*$/, '').trim();
    if (el.name) return el.name;
    const tid = el.getAttribute?.('data-testid');
    if (tid) return tid;
  } catch (_) { /* swallow */ }
  return null;
}

/**
 * Small live summary of invalid required fields, rendered above the Save button.
 *
 * Props:
 *   formRef:       React ref to the <form>
 *   triggerNonce:  change this number after a failed submit to force the
 *                  summary to appear (it otherwise lies dormant until first
 *                  submit attempt — pre-submit we don't nag the user).
 */
export default function ValidationSummary({ formRef, triggerNonce = 0 }) {
  const [items, setItems] = useState([]);
  const [armed, setArmed] = useState(false);

  const recompute = useCallback(() => {
    const form = formRef?.current;
    if (!form) { setItems([]); return; }
    let invalid;
    try {
      invalid = Array.from(form.querySelectorAll(':invalid'));
    } catch (_) { invalid = []; }
    const seen = new Set();
    const rows = [];
    for (const el of invalid) {
      // Skip disabled/hidden inputs defensively.
      if (el.disabled) continue;
      const label = resolveLabel(el);
      if (!label) continue;
      const key = el.id || el.getAttribute?.('data-testid') || `${el.tagName}-${rows.length}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({ key, label, el });
    }
    setItems(rows);
  }, [formRef]);

  // Arm on first failed submit and run once.
  useEffect(() => {
    if (triggerNonce > 0) {
      setArmed(true);
      recompute();
    }
  }, [triggerNonce, recompute]);

  // Once armed, re-check on any form change / blur so entries disappear live.
  useEffect(() => {
    if (!armed) return undefined;
    const form = formRef?.current;
    if (!form) return undefined;
    const handler = () => recompute();
    form.addEventListener('input', handler);
    form.addEventListener('change', handler);
    form.addEventListener('blur', handler, true);
    return () => {
      form.removeEventListener('input', handler);
      form.removeEventListener('change', handler);
      form.removeEventListener('blur', handler, true);
    };
  }, [armed, formRef, recompute]);

  if (!armed || items.length === 0) return null;

  const jumpTo = (el) => {
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (_) {} }, 250);
    } catch (_) { /* swallow */ }
  };

  return (
    <div
      className="bg-[#C44545]/10 border border-[#C44545]/40 rounded-sm p-3 mb-4"
      data-testid="validation-summary"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-[#C44545] mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-[#C44545] uppercase tracking-wider mb-1.5">
            Complete these to save:
          </div>
          <ul className="flex flex-wrap gap-1.5" data-testid="validation-summary-list">
            {items.map((it) => (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={() => jumpTo(it.el)}
                  className="px-2 py-0.5 text-xs text-[#EAE7E1] bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#C44545]/30 hover:border-[#C44545]/60 rounded-sm transition-colors"
                  data-testid={`validation-summary-item-${it.key}`}
                >
                  {it.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
